'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useId, useImperativeHandle, useRef, useState, } from 'react';
import { cx } from '../../../internal/cx.js';
import { ClockGlyph } from '../../../internal/icons.js';
import { formatTime, localeHourCycle, parseIsoDateTime, parseTimeInput, toIsoDateTime, useToday, } from '../../../internal/dates/index.js';
import { useFieldContext, useFieldControl } from '../../forms/form-field/index.js';
import { DatePicker, } from '../date-picker/index.js';
const DEFAULT_MESSAGES = {
    date: 'date',
    time: 'time',
    invalidTime: (example) => `Enter a time like ${example}.`,
    timeBeforeMin: (min) => `Choose ${min} or later.`,
    timeAfterMax: (max) => `Choose ${max} or earlier.`,
};
/**
 * A date and a wall-clock time in one field: a DatePicker plus a typed time
 * in the locale's 12- or 24-hour clock, with arrow-key steps and a slot for
 * the time zone. The value is `YYYY-MM-DDTHH:mm`, the same as
 * `<input type="datetime-local">`, so it never shifts with the device's zone.
 */
export const DateTimePicker = forwardRef(function DateTimePicker({ value: valueProp, defaultValue = null, onValueChange, min, max, isDateDisabled, step = 15, hourCycle, defaultTime = '09:00', timeZone, presets, locale, weekStartsOn, today: todayProp, size = 'md', disabled, readOnly, required, invalid, name, onValidationChange, messages: messagesProp, className, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, ...rest }, ref) {
    const messages = { ...DEFAULT_MESSAGES, ...messagesProp };
    const today = useToday(todayProp);
    const id = useId();
    const rootRef = useRef(null);
    useImperativeHandle(ref, () => rootRef.current);
    const field = useFieldContext();
    const cycle = hourCycle ?? localeHourCycle(locale);
    const [inner, setInner] = useState(defaultValue);
    const value = valueProp !== undefined ? valueProp : inner;
    const parsed = value ? parseIsoDateTime(value) : null;
    const date = parsed?.date ?? null;
    const minutes = parsed?.minutes ?? null;
    const emit = (next) => {
        if (valueProp === undefined)
            setInner(next);
        onValueChange?.(next);
    };
    const minParts = min ? parseIsoDateTime(min) : null;
    const maxParts = max ? parseIsoDateTime(max) : null;
    /** The allowed minutes on a given day. */
    const bounds = (d) => ({
        lo: minParts && d === minParts.date ? minParts.minutes : 0,
        hi: maxParts && d === maxParts.date ? maxParts.minutes : 1439,
    });
    const shownTime = minutes === null ? '' : formatTime(minutes, locale, cycle);
    const [timeText, setTimeText] = useState(shownTime);
    const editing = useRef(false);
    const [timeError, setTimeError] = useState(null);
    const [dateError, setDateError] = useState(null);
    useEffect(() => {
        if (editing.current)
            return;
        setTimeText(shownTime);
        setTimeError(null);
    }, [shownTime]);
    const error = dateError ?? (timeError === 'invalid-time' ? 'invalid-time' : timeError);
    const reported = useRef(null);
    const validationRef = useRef(onValidationChange);
    validationRef.current = onValidationChange;
    useEffect(() => {
        if (reported.current === error)
            return;
        reported.current = error;
        validationRef.current?.(error);
    }, [error]);
    const fallbackMinutes = parseTimeInput(defaultTime) ?? 540;
    const onDate = (d) => {
        if (!d) {
            emit(null);
            return;
        }
        const { lo, hi } = bounds(d);
        const m = Math.min(hi, Math.max(lo, minutes ?? parseTimeInput(timeText, locale) ?? fallbackMinutes));
        editing.current = false;
        setTimeError(null);
        emit(toIsoDateTime(d, m));
    };
    const applyTime = (m) => {
        const d = date ?? (minParts && today < minParts.date ? minParts.date : today);
        const { lo, hi } = bounds(d);
        if (m < lo || m > hi) {
            setTimeError(m < lo ? 'before-min' : 'after-max');
            return;
        }
        editing.current = false;
        setTimeError(null);
        setTimeText(formatTime(m, locale, cycle));
        const next = toIsoDateTime(d, m);
        if (next !== value)
            emit(next);
    };
    const commitTime = () => {
        const t = timeText.trim();
        if (!t) {
            editing.current = false;
            // An empty time with a date is not a complete value; restore the last time.
            setTimeText(shownTime);
            setTimeError(null);
            return;
        }
        const m = parseTimeInput(t, locale);
        if (m === null) {
            setTimeError('invalid-time');
            return;
        }
        applyTime(m);
    };
    const onTimeKeyDown = (event) => {
        if (event.key === 'Enter')
            commitTime();
        if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')
            return;
        if (readOnly)
            return;
        event.preventDefault();
        const s = Math.max(1, Math.round(step));
        const current = parseTimeInput(timeText, locale) ?? minutes ?? fallbackMinutes;
        const next = event.key === 'ArrowUp'
            ? (Math.floor(current / s) + 1) * s
            : (Math.ceil(current / s) - 1) * s;
        applyTime(((next % 1440) + 1440) % 1440);
    };
    const outerLabel = field?.labelId ?? ariaLabelledBy;
    const datePartId = `${id}-date`;
    const timePartId = `${id}-time`;
    const timeErrorId = `${id}-time-error`;
    const timeInputId = `${id}-time-input`;
    const example = formatTime(870, locale, cycle);
    const timeMessage = timeError === 'invalid-time'
        ? messages.invalidTime(example)
        : timeError === 'before-min' && minParts
            ? messages.timeBeforeMin(formatTime(minParts.minutes, locale, cycle))
            : timeError === 'after-max' && maxParts
                ? messages.timeAfterMax(formatTime(maxParts.minutes, locale, cycle))
                : null;
    const timeControl = useFieldControl({
        id: timeInputId,
        disabled,
        required,
        'aria-describedby': timeMessage ? timeErrorId : undefined,
    }, { invalid: invalid || !!timeError || undefined });
    return (_jsxs("div", { ref: rootRef, role: "group", "aria-label": ariaLabel, "aria-labelledby": field?.labelId ?? ariaLabelledBy, ...rest, className: cx('mn-date-time-picker', className), "data-size": size, "data-disabled": timeControl.disabled || undefined, children: [_jsx("span", { id: datePartId, hidden: true, children: messages.date }), _jsx("span", { id: timePartId, hidden: true, children: messages.time }), _jsx(DatePicker, { className: "mn-date-time-picker__date", value: date, onValueChange: onDate, min: minParts?.date, max: maxParts?.date, isDateDisabled: isDateDisabled, presets: presets, locale: locale, weekStartsOn: weekStartsOn, today: todayProp, size: size, disabled: disabled, readOnly: readOnly, required: required, invalid: invalid, onValidationChange: setDateError, messages: messagesProp, "aria-labelledby": outerLabel ? `${outerLabel} ${datePartId}` : datePartId }), _jsxs("div", { className: "mn-date-time-picker__time", children: [_jsxs("div", { className: "mn-input mn-date-time-picker__time-box", "data-size": size, "data-invalid": timeControl['aria-invalid'] ? '' : undefined, "data-disabled": timeControl.disabled || undefined, children: [_jsx(ClockGlyph, { className: "mn-date-time-picker__icon" }), _jsx("input", { ...timeControl, type: "text", inputMode: "text", autoComplete: "off", spellCheck: false, readOnly: readOnly, className: "mn-date-time-picker__input", "aria-labelledby": outerLabel ? `${outerLabel} ${timePartId}` : timePartId, placeholder: example, value: timeText, onChange: (e) => {
                                    editing.current = true;
                                    setTimeText(e.target.value);
                                }, onBlur: commitTime, onKeyDown: onTimeKeyDown })] }), timeMessage ? (_jsx("p", { id: timeErrorId, className: "mn-field__error mn-date-time-picker__error", "aria-live": "polite", children: timeMessage })) : null] }), timeZone ? _jsx("div", { className: "mn-date-time-picker__zone", children: timeZone }) : null, name ? (_jsx("input", { type: "hidden", name: name, value: value ?? '', disabled: timeControl.disabled })) : null] }));
});
//# sourceMappingURL=date-time-picker.js.map